import { ArrowsUpDownIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Icon } from '../Icon';

type SortDirection = 'asc' | 'desc' | false;

const nextSortTooltipKey = (direction: SortDirection) => {
    if (direction === 'asc') return 'HIT_LIST.SORT_DESCENDING';
    if (direction === 'desc') return 'HIT_LIST.SORT_CLEAR';
    return 'HIT_LIST.SORT_ASCENDING';
};

const GridSortingCaret = ({
    direction,
    index,
    variant = 'chevron',
}: {
    direction: SortDirection;
    index: number;
    variant?: 'chevron' | 'arrows';
}) => {
    const { t } = useTranslation();

    // LAX-10059: hit list / catalog use one ArrowsUpDown icon. It stays
    // hidden until the header cell is hovered, then stays visible once a
    // direction is applied. The tooltip names the next click in the cycle.
    // The priority number is the multi-column sort order (Shift+click);
    // dropping it made a second sorted column look like a single sort.
    if (variant === 'arrows') {
        return (
            <span
                title={t(nextSortTooltipKey(direction))}
                className={cn(
                    'tw-ml-auto tw-inline-flex tw-shrink-0 tw-items-center',
                    // Opacity, not visibility: a hidden icon is not a hover
                    // target, so the native tooltip never opens on the
                    // default (unsorted) state.
                    !direction && 'tw-opacity-0 group-hover/colhead:tw-opacity-100',
                )}
            >
                <ArrowsUpDownIcon className="tw-w-5 tw-aspect-square tw-icon-primary" />
                {direction && (
                    <span className="tw-text-xs tw-mx-1 tw-tabular-nums">{index + 1}</span>
                )}
            </span>
        );
    }

    if (!direction) {
        return null;
    }

    return direction === 'asc' ?
        <>
            <Icon className="tw-ml-auto" icon={ChevronUpIcon} size="md" />
            <span className="tw-text-xs tw-mx-1">{index + 1}</span>
        </>
        :
        <>
            <Icon className="tw-ml-auto" icon={ChevronDownIcon} size="md" />
            <span className="tw-text-xs tw-mx-1">{index + 1}</span>
        </>;
};

export { GridSortingCaret };
